import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { adminService, Challenge, ChallengeTranslation, FrequencyPattern } from '../services/adminService'
import { translationService } from '../services/translationService'
import { useLanguages } from '../hooks/useLanguages'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { LanguageTabs } from '../components/LanguageTabs'
import { DeepSeekSetupNotice } from '../components/DeepSeekSetupNotice'
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal'
import { FrequencyBuilder } from '../components/FrequencyBuilder'

interface ChallengeTranslationFormData {
  translations: Record<string, { name: string; description: string; slug?: string }>
  category: string
  defaultFrequency: FrequencyPattern
  status: string
  totalUsers: number
}

interface TranslationProgress {
  language: string
  status: 'pending' | 'translating' | 'completed' | 'error' | 'saving'
  startTime?: number
  endTime?: number
  error?: string
  translatedText?: string
}

interface AITranslationModalProps {
  availableLanguages: Array<{ code: string; name: string }>
  existingTranslations: string[]
  onTranslate: (sourceLanguage: string, targetLanguages: string[], maxConcurrency?: number) => void
  onClose: () => void
}

function AITranslationModal({ availableLanguages, existingTranslations, onTranslate, onClose }: AITranslationModalProps) {
  const { t } = useTranslation()
  const [sourceLanguage, setSourceLanguage] = useState('')
  const [targetLanguages, setTargetLanguages] = useState<string[]>([])

  const availableSourceLanguages = availableLanguages.filter(lang => 
    existingTranslations.includes(lang.code)
  )

  const availableTargetLanguages = availableLanguages.filter(lang => 
    !existingTranslations.includes(lang.code) && lang.code !== sourceLanguage
  )

  const handleTargetLanguageToggle = (languageCode: string) => {
    setTargetLanguages(prev => 
      prev.includes(languageCode)
        ? prev.filter(code => code !== languageCode)
        : [...prev, languageCode]
    )
  }

  const handleTranslate = () => {
    if (!sourceLanguage) {
      toast.error(t('challengeEdit.aiTranslationModal.sourceLanguageRequired'))
      return
    }

    if (targetLanguages.length === 0) {
      toast.error(t('challengeEdit.aiTranslationModal.targetLanguagesRequired'))
      return
    }

    onTranslate(sourceLanguage, targetLanguages, 3) // Fixed max concurrency
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('challengeEdit.aiTranslationModal.title')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Source Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('challengeEdit.aiTranslationModal.sourceLanguage')}
            </label>
            <select
              value={sourceLanguage}
              onChange={(e) => setSourceLanguage(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">{t('challengeEdit.aiTranslationModal.selectSourceLanguage')}</option>
              {availableSourceLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
          </div>

          {/* Target Languages */}
          {sourceLanguage && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('challengeEdit.aiTranslationModal.targetLanguagesSelected', { count: targetLanguages.length })}
                </label>
                <button
                  type="button"
                  onClick={() => {
                    if (targetLanguages.length === availableTargetLanguages.length) {
                      setTargetLanguages([])
                    } else {
                      setTargetLanguages(availableTargetLanguages.map(l => l.code))
                    }
                  }}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {targetLanguages.length === availableTargetLanguages.length ? t('challengeEdit.aiTranslationModal.deselectAll') : t('challengeEdit.aiTranslationModal.selectAll')}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {availableTargetLanguages.map((lang) => (
                  <label key={lang.code} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={targetLanguages.includes(lang.code)}
                      onChange={() => handleTargetLanguageToggle(lang.code)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm">{lang.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
          >
            {t('challengeEdit.aiTranslationModal.cancel')}
          </button>
          <button
            onClick={handleTranslate}
            disabled={!sourceLanguage || targetLanguages.length === 0}
            className="px-4 py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {t('challengeEdit.aiTranslationModal.startTranslation')}
          </button>
        </div>
      </div>
    </div>
  )
}

function TranslationProgressModal({ 
  progress, 
  onClose 
}: { 
  progress: Record<string, TranslationProgress>, 
  onClose: () => void 
}) {
  const { t } = useTranslation()
  const progressEntries = Object.entries(progress)
  const completed = progressEntries.filter(([, p]) => p.status === 'completed').length
  const total = progressEntries.length

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('challengeEdit.translationProgress.title', { completed, total })}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {progressEntries.map(([language, prog]) => {
            const duration = prog.startTime && prog.endTime 
              ? ((prog.endTime - prog.startTime) / 1000).toFixed(1) 
              : null

            return (
              <div key={language} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    prog.status === 'completed' ? 'bg-green-500' :
                    prog.status === 'translating' || prog.status === 'saving' ? 'bg-blue-500 animate-pulse' :
                    prog.status === 'error' ? 'bg-red-500' :
                    'bg-gray-300'
                  }`} />
                  <span className="font-medium">{language}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-600">
                    {prog.status === 'completed' && duration && `${duration}s`}
                    {prog.status === 'translating' && t('challengeEdit.translationProgress.translating')}
                    {prog.status === 'saving' && t('challengeEdit.translationProgress.saving')}
                    {prog.status === 'error' && t('challengeEdit.translationProgress.failed')}
                    {prog.status === 'pending' && t('challengeEdit.translationProgress.waiting')}
                  </div>
                  {prog.error && (
                    <div className="text-xs text-red-500">{prog.error}</div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(completed / total) * 100}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// Helper function to generate slug from text
const generateSlug = (text: string): string => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single
    .trim() // Remove leading/trailing spaces
    .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
}

export function ChallengeEditPage() {
  const params = useParams({ strict: false }) as { challengeId?: string }
  const challengeId = params.challengeId
  const isCreateMode = !challengeId
  console.log('🎯 ChallengeEditPage received challengeId:', challengeId, 'isCreateMode:', isCreateMode)
  
  const navigate = useNavigate()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  
  const [activeTab, setActiveTab] = useState('en')
  const [showAIModal, setShowAIModal] = useState(false)
  const [showProgressModal, setShowProgressModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [translationProgress, setTranslationProgress] = useState<Record<string, TranslationProgress>>({})

  // Get dynamic languages from database
  const { languages: dynamicLanguages } = useLanguages()
  const SUPPORTED_LANGUAGES = useMemo(() => 
    dynamicLanguages.map(lang => ({
      code: lang.code,
      name: lang.name
    })), [dynamicLanguages]
  )

  // Load challenge data (only when editing)
  const { data: challenge, isLoading: challengeLoading, error: challengeError } = useQuery({
    queryKey: ['challenge', challengeId],
    queryFn: async () => {
      console.log('Loading challenge with ID:', challengeId)
      const challenge = await adminService.getChallenge(challengeId!)
      if (!challenge) {
        console.warn(`Challenge with ID "${challengeId}" not found`)
      } else {
        console.log('Challenge loaded successfully:', { id: challenge.id, name: challenge.name })
      }
      return challenge
    },
    enabled: !isCreateMode && !!challengeId,
  })

  // Load challenge translations (only when editing)
  const { data: translations = [], isLoading: translationsLoading } = useQuery({
    queryKey: ['challengeTranslations', challengeId],
    queryFn: () => adminService.getChallengeTranslations(challengeId!),
    enabled: !isCreateMode && !!challengeId,
  })

  // Load categories for form
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => adminService.getCategories('en'),
  })

  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<ChallengeTranslationFormData>({
    defaultValues: {
      translations: {},
      category: '',
      defaultFrequency: {
        mode: 'daily',
        times: ['09:00'],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      status: 'active',
      totalUsers: 0,
    }
  })

  const watchedTranslations = watch('translations')
  const watchedFrequency = watch('defaultFrequency')

  // Initialize form with existing data or defaults for create mode
  useEffect(() => {
    if (isCreateMode) {
      // Initialize with default values for create mode
      setValue('category', '')
      setValue('defaultFrequency', {
        mode: 'daily',
        times: ['09:00'],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      setValue('status', 'active')
      setValue('totalUsers', 0)

      // Initialize empty translations for all supported languages
      const translationsMap: Record<string, { name: string; description: string }> = {}
      SUPPORTED_LANGUAGES.forEach(lang => {
        translationsMap[lang.code] = {
          name: '',
          description: ''
        }
      })
      setValue('translations', translationsMap)
    } else if (challenge && translations.length >= 0) {
      // Initialize with challenge data for edit mode
      setValue('category', challenge.category || '')
      setValue('defaultFrequency', challenge.defaultFrequency || {
        mode: 'daily',
        times: ['09:00'],
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      setValue('status', challenge.status || 'active')
      setValue('totalUsers', challenge.totalUsers || 0)

      // Initialize translations - ensure all supported languages have entries (even if empty)
      const translationsMap: Record<string, { name: string; description: string; slug?: string }> = {}
      
      // Initialize empty values for all supported languages first
      SUPPORTED_LANGUAGES.forEach(lang => {
        translationsMap[lang.code] = {
          name: '',
          description: ''
        }
      })
      
      // Add existing translations (overwriting empty values where data exists)
      translations.forEach(translation => {
        translationsMap[translation.language] = {
          name: translation.name,
          description: translation.description
        }
      })

      // If no translations exist but challenge has name/description, use as English
      if (translations.length === 0 && challenge.name) {
        translationsMap.en = {
          name: challenge.name,
          description: challenge.description || ''
        }
      }

      setValue('translations', translationsMap)
    }
  }, [isCreateMode, challenge, translations, setValue, SUPPORTED_LANGUAGES])

  // Handle tab switching - ensure correct values are shown for the active language
  useEffect(() => {
    const currentTranslations = watchedTranslations[activeTab]
    if (currentTranslations) {
      // Fields should already be populated correctly, but ensure they're not showing wrong data
      setValue(`translations.${activeTab}.name`, currentTranslations.name || '')
      setValue(`translations.${activeTab}.description`, currentTranslations.description || '')
    } else {
      // Explicitly clear fields for languages without data
      setValue(`translations.${activeTab}.name`, '')
      setValue(`translations.${activeTab}.description`, '')
    }
  }, [activeTab, watchedTranslations, setValue])

  // Auto-generate slugs when challenge names change
  useEffect(() => {
    SUPPORTED_LANGUAGES.forEach(lang => {
      const currentTranslation = watchedTranslations[lang.code]
      if (currentTranslation?.name && !currentTranslation?.slug) {
        const autoSlug = generateSlug(currentTranslation.name)
        if (autoSlug) {
          setValue(`translations.${lang.code}.slug`, autoSlug)
        }
      }
    })
  }, [watchedTranslations, setValue, SUPPORTED_LANGUAGES])

  // Mutations
  const createChallengeMutation = useMutation({
    mutationFn: (challenge: Omit<Challenge, 'id' | 'createdAt' | 'updatedAt'>) =>
      adminService.createChallenge(challenge),
    onSuccess: (newChallenge) => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] })
      toast.success(t('challengeEdit.notifications.challengeCreated'))
      navigate({ to: `/challenges/${newChallenge}` })
    },
    onError: () => {
      toast.error(t('challengeEdit.notifications.challengeCreateFailed'))
    },
  })

  const updateChallengeMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Challenge> }) =>
      adminService.updateChallenge(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challenge', challengeId] })
      toast.success(t('challengeEdit.notifications.challengeUpdated'))
    },
    onError: () => {
      toast.error(t('challengeEdit.notifications.challengeUpdateFailed'))
    },
  })

  const deleteChallengeMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteChallenge(id),
    onSuccess: () => {
      toast.success(t('challengeEdit.notifications.challengeDeleted'))
      navigate({ to: '/challenges' })
    },
    onError: () => {
      toast.error(t('challengeEdit.notifications.challengeDeleteFailed'))
    },
  })

  const createTranslationMutation = useMutation({
    mutationFn: (translation: Omit<ChallengeTranslation, 'id'>) =>
      adminService.createChallengeTranslation(translation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challengeTranslations', challengeId] })
    },
  })

  const updateTranslationMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<ChallengeTranslation> }) =>
      adminService.updateChallengeTranslation(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['challengeTranslations', challengeId] })
    },
  })

  // Handle form submission
  const onSubmit = async (data: ChallengeTranslationFormData) => {
    console.log('Submitting form data:', data, 'isCreateMode:', isCreateMode)

    try {
      if (isCreateMode) {
        // Create new challenge with English translation
        const englishTranslation = data.translations.en
        if (!englishTranslation?.name) {
          toast.error('Challenge name in English is required')
          return
        }

        const newChallenge = await createChallengeMutation.mutateAsync({
          name: englishTranslation.name,
          description: englishTranslation.description || '',
          category: data.category,
          defaultFrequency: data.defaultFrequency,
          status: data.status as any,
          totalUsers: data.totalUsers,
        })

        console.log('Challenge created successfully:', newChallenge)

        // Create translations for all languages (including English)
        for (const [language, content] of Object.entries(data.translations)) {
          if (content.name) {
            const slug = content.slug || generateSlug(content.name)
            await createTranslationMutation.mutateAsync({
              challengeId: newChallenge,
              language,
              name: content.name,
              description: content.description,
              slug: slug,
            })
          }
        }
      } else {
        // Update existing challenge
        if (!challenge) return

        // Update challenge basic info
        console.log('Updating challenge with:', {
          id: challenge.id,
          updates: {
            category: data.category,
            defaultFrequency: data.defaultFrequency,
            status: data.status as any,
          }
        })

        await updateChallengeMutation.mutateAsync({
          id: challenge.id,
          updates: {
            category: data.category,
            defaultFrequency: data.defaultFrequency,
            status: data.status as any,
            totalUsers: data.totalUsers,
          }
        })

        console.log('Challenge updated successfully')

        // Update translations
        for (const [language, content] of Object.entries(data.translations)) {
          if (content.name || content.description) {
            const existingTranslation = translations.find(t => t.language === language)
            const slug = content.slug || generateSlug(content.name || '')
            
            console.log(`Updating translation for ${language}:`, content)
            
            if (existingTranslation) {
              await updateTranslationMutation.mutateAsync({
                id: existingTranslation.id,
                updates: {
                  name: content.name,
                  description: content.description,
                  slug: slug,
                }
              })
            } else {
              await createTranslationMutation.mutateAsync({
                challengeId: challenge.id,
                language,
                name: content.name,
                description: content.description,
                slug: slug,
              })
            }
          }
        }

        toast.success(t('challengeEdit.notifications.allChangesSaved'))
      }
    } catch (error) {
      console.error('Save error:', error)
      
      // Provide specific error messages for common issues
      if (error instanceof Error) {
        if (error.message.includes('does not exist in the database')) {
          toast.error(t('challengeEdit.notifications.challengeNotExists'))
          setTimeout(() => navigate({ to: '/challenges' }), 2000)
        } else if (error.message.includes('No document to update')) {
          toast.error(t('challengeEdit.notifications.challengeNotFoundDb'))
          setTimeout(() => navigate({ to: '/challenges' }), 2000)
        } else {
          toast.error(`${t('challengeEdit.notifications.saveChangesFailed')}: ${error.message}`)
        }
      } else {
        toast.error(t('challengeEdit.notifications.saveChangesUnknownError'))
      }
    }
  }

  // Handle AI translation
  const handleAITranslate = async (sourceLanguage: string, targetLanguages: string[], maxConcurrency: number = 3) => {
    const sourceTranslation = watchedTranslations[sourceLanguage]
    
    if (!sourceTranslation?.name || !sourceTranslation?.description) {
      toast.error(t('challengeEdit.aiTranslationModal.sourceContentRequired'))
      return
    }

    // Initialize progress tracking
    const progress: Record<string, TranslationProgress> = {}
    targetLanguages.forEach(lang => {
      progress[lang] = {
        language: lang,
        status: 'pending',
      }
    })
    setTranslationProgress(progress)
    setShowProgressModal(true)

    // Configure translation service
    translationService.setConfig({
      temperature: 1.3,
      useJsonMode: true
    })

    // Prepare translation requests
    const requests = targetLanguages.map(lang => ({
      sourceLanguage,
      targetLanguage: lang,
      text: {
        name: sourceTranslation.name,
        description: sourceTranslation.description
      },
      temperature: 1.3,
      useJsonMode: true
    }))

    // Start translations with progress tracking
    targetLanguages.forEach(lang => {
      setTranslationProgress(prev => ({
        ...prev,
        [lang]: { ...prev[lang], status: 'translating', startTime: Date.now() }
      }))
    })

    try {
      await translationService.translateChallengeParallel(
        requests,
        async (completed, _, result, error, requestIndex) => {
          const lang = targetLanguages[requestIndex!]
          
          if (error) {
            setTranslationProgress(prev => ({
              ...prev,
              [lang]: { 
                ...prev[lang], 
                status: 'error', 
                endTime: Date.now(),
                error: error instanceof Error ? error.message : 'Translation failed'
              }
            }))
            
            const languageName = SUPPORTED_LANGUAGES.find(l => l.code === lang)?.name || lang
            toast.error(`Translation to ${languageName} failed`)
          } else if (result) {
            // Generate slug for translated content
            const autoSlug = result.slug || generateSlug(result.name)
            
            // Update form with translated content
            setValue(`translations.${lang}`, {
              name: result.name,
              description: result.description,
              slug: autoSlug
            })

            // Update progress to saving
            setTranslationProgress(prev => ({
              ...prev,
              [lang]: { 
                ...prev[lang], 
                status: 'saving'
              }
            }))

            // Auto-save translation to database
            try {
              const existingTranslation = translations.find(t => t.language === lang)
              
              if (existingTranslation) {
                await updateTranslationMutation.mutateAsync({
                  id: existingTranslation.id,
                  updates: {
                    name: result.name,
                    description: result.description,
                    slug: autoSlug,
                  }
                })
              } else {
                await createTranslationMutation.mutateAsync({
                  challengeId: challengeId!,
                  language: lang,
                  name: result.name,
                  description: result.description,
                  slug: autoSlug,
                })
              }

              // Mark as completed
              setTranslationProgress(prev => ({
                ...prev,
                [lang]: { 
                  ...prev[lang], 
                  status: 'completed', 
                  endTime: Date.now(),
                  translatedText: result.name
                }
              }))

              const languageName = SUPPORTED_LANGUAGES.find(l => l.code === lang)?.name || lang
              toast.success(`${languageName} translation saved successfully`)
            } catch (saveError) {
              setTranslationProgress(prev => ({
                ...prev,
                [lang]: { 
                  ...prev[lang], 
                  status: 'error', 
                  endTime: Date.now(),
                  error: 'Failed to save translation'
                }
              }))
              toast.error(`Failed to save ${lang} translation`)
            }
          }
        },
        maxConcurrency
      )
    } catch (error) {
      toast.error('Translation process failed')
    }
  }

  // Handle challenge deletion
  const handleDelete = () => {
    setShowDeleteConfirm(true)
  }

  const confirmDelete = () => {
    deleteChallengeMutation.mutate(challengeId!)
    setShowDeleteConfirm(false)
  }

  if (!isCreateMode && (challengeLoading || translationsLoading)) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('challengeEdit.loadingChallenge')}</div>
        </div>
      </div>
    )
  }

  if (challengeError) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">{t('challengeEdit.errorLoadingChallenge')}</h1>
          <p className="text-gray-600 mb-4">
            {challengeError instanceof Error ? challengeError.message : 'Failed to load challenge'}
          </p>
          <button
            onClick={() => navigate({ to: '/challenges' })}
            className="text-primary-600 hover:text-primary-700"
          >
            {t('challengeEdit.backToChallenges')}
          </button>
        </div>
      </div>
    )
  }

  if (!isCreateMode && !challenge && !challengeLoading) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('challengeEdit.challengeNotFound')}</h1>
          <p className="text-gray-600 mb-4">
            {t('challengeEdit.challengeNotFoundMessage', { challengeId })}
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-medium text-yellow-800 mb-2">🔍 Debugging Information</h3>
            <div className="text-sm text-yellow-700 space-y-1">
              <p><strong>Requested Challenge ID:</strong> {challengeId}</p>
              <p><strong>Current URL:</strong> {window.location.href}</p>
            </div>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => navigate({ to: '/challenges' })}
              className="bg-primary-600 text-white px-4 py-2 rounded-full hover:bg-primary-700 transition-colors"
            >
              {t('challengeEdit.backToChallenges')}
            </button>
            <div>
              <button
                onClick={() => window.location.reload()}
                className="text-gray-600 hover:text-gray-800"
              >
                {t('challengeEdit.reloadPage')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const existingLanguages = translations.map(t => t.language)
  if (watchedTranslations.en && !existingLanguages.includes('en')) {
    existingLanguages.push('en')
  }

  return (
    <div className="p-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left side - Back button and title */}
          <div className="flex flex-col items-start gap-2">
            <button
              onClick={() => navigate({ to: '/challenges' })}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-200 bg-gray-50 rounded-lg transition-all duration-200 group"
              aria-label={t('challengeEdit.back')}
            >
              <svg 
                className="w-5 h-5 transition-transform group-hover:-translate-x-1" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">{t('challengeEdit.back')}</span>
            </button>
            
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                {isCreateMode 
                  ? t('challengeEdit.createChallengeTitle') 
                  : t('challengeEdit.editChallengeTitle', { name: challenge?.name || 'Challenge' })
                }
              </h1>
              <p className="text-gray-600 mt-2 text-sm lg:text-base leading-relaxed">
                {isCreateMode 
                  ? t('challengeEdit.createSubtitle') 
                  : t('challengeEdit.subtitle')
                }
              </p>
            </div>
          </div>

          {/* Right side - Action buttons */}
          {!isCreateMode && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowAIModal(true)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {t('challengeEdit.aiTranslate')}
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 transition-all duration-200 font-medium text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                {t('challengeEdit.deleteChallenge')}
              </button>
            </div>
          )}
        </div>
      </div>

      <DeepSeekSetupNotice />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Challenge Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">{t('challengeEdit.basicInformation')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('challengeEdit.category')}
              </label>
              <select
                {...register('category', { required: 'Category is required' })}
                className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">{t('challengeEdit.selectCategory')}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.icon} {category.name}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                {t('challengeEdit.defaultFrequency')}
              </label>
              <FrequencyBuilder
                value={watchedFrequency}
                onChange={(frequency) => setValue('defaultFrequency', frequency)}
              />
              {errors.defaultFrequency && (
                <p className="text-red-500 text-sm mt-1">{errors.defaultFrequency.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('challengeEdit.status')}
              </label>
              <select
                {...register('status', { required: 'Status is required' })}
                className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent h-10"
              >
                <option value="active">{t('challengeEdit.active')}</option>
                <option value="inactive">{t('challengeEdit.inactive')}</option>
                <option value="draft">{t('challengeEdit.draft')}</option>
              </select>
              {errors.status && (
                <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('challengeEdit.totalUsers')}
              </label>
              <input
                type="number"
                min="0"
                {...register('totalUsers', { 
                  required: 'Total users is required',
                  min: { value: 0, message: 'Total users must be 0 or greater' },
                  valueAsNumber: true
                })}
                className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent h-10"
                placeholder="0"
              />
              {errors.totalUsers && (
                <p className="text-red-500 text-sm mt-1">{errors.totalUsers.message}</p>
              )}
            </div>




          </div>
        </div>

        {/* Multi-language Content */}
        <div className="bg-white rounded-lg shadow">
          <LanguageTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            translations={watchedTranslations}
          />

          <div className="p-6">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('challengeEdit.challengeName')} ({SUPPORTED_LANGUAGES.find(l => l.code === activeTab)?.name})
                </label>
                <input
                  type="text"
                  {...register(`translations.${activeTab}.name`)}
                  placeholder={t('challengeEdit.challengeNamePlaceholder')}
                  className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('challengeEdit.challengeDescription')} ({SUPPORTED_LANGUAGES.find(l => l.code === activeTab)?.name})
                </label>
                <textarea
                  {...register(`translations.${activeTab}.description`)}
                  rows={4}
                  placeholder={t('challengeEdit.challengeDescriptionPlaceholder')}
                  className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Slug input hidden - automatically generated from challenge name */}
              {false && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('challengeEdit.challengeSlug')} ({SUPPORTED_LANGUAGES.find(l => l.code === activeTab)?.name}) 
                  <span className="text-xs text-gray-500 font-normal ml-1">- Auto-generated</span>
                </label>
                <input
                  type="text"
                  {...register(`translations.${activeTab}.slug`, {
                    pattern: {
                      value: /^[a-z0-9-]+$/,
                      message: 'Slug can only contain lowercase letters, numbers, and hyphens'
                    }
                  })}
                  placeholder={t('challengeEdit.challengeSlugPlaceholder')}
                  className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Automatically generated from the challenge name. You can edit if needed.
                </p>
                {errors.translations?.[activeTab]?.slug && (
                  <p className="text-red-500 text-sm mt-1">{errors.translations[activeTab].slug.message}</p>
                )}
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Errors Display */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-red-800 mb-2">{t('challengeEdit.fixErrors')}</h3>
            <ul className="list-disc list-inside text-sm text-red-700">
              {Object.entries(errors).map(([field, error]) => (
                <li key={field}>
                  <strong>{field}:</strong> {(error as any)?.message || t('challengeEdit.invalidField')}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={
              (isCreateMode ? createChallengeMutation.isPending : updateChallengeMutation.isPending) || 
              Object.keys(errors).length > 0
            }
            className="px-6 py-3 bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {(isCreateMode ? createChallengeMutation.isPending : updateChallengeMutation.isPending) 
              ? t('challengeEdit.saving') 
              : (isCreateMode 
                ? t('challengeEdit.createChallenge') 
                : t('challengeEdit.saveAllChanges'))
            }
          </button>
        </div>
      </form>

      {/* AI Translation Modal */}
      {showAIModal && (
        <AITranslationModal
          availableLanguages={SUPPORTED_LANGUAGES}
          existingTranslations={existingLanguages}
          onTranslate={handleAITranslate}
          onClose={() => setShowAIModal(false)}
        />
      )}

      {/* Translation Progress Modal */}
      {showProgressModal && (
        <TranslationProgressModal
          progress={translationProgress}
          onClose={() => setShowProgressModal(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {!isCreateMode && challenge && (
        <DeleteConfirmationModal
          isOpen={showDeleteConfirm}
          title={t('challengeEdit.deleteConfirmTitle')}
          message={t('challengeEdit.deleteConfirmMessage', { name: challenge.name })}
          itemName={challenge.name}
          warningText={t('challengeEdit.deleteWarning')}
          onConfirm={confirmDelete}
          onClose={() => setShowDeleteConfirm(false)}
          isLoading={deleteChallengeMutation.isPending}
        />
      )}
    </div>
  )
} 